import {
  CACHE_EXPIRY_MS,
  STORAGE_KEY_IMAGES,
  STORAGE_KEY_ITEMS,
  STORAGE_KEY_PRODUCT_DATA,
  STORAGE_KEY_WISHLISTS
} from './wishlist.types';

const IS_DEV = import.meta.env.DEV;

export type CachedData<T> = {
  data: T;
  expiry: number;
};

/**
 * Generic function to load data from localStorage cache
 * @param key - localStorage key
 * @returns cached data or null if not found/expired
 */
export function loadFromCache<T>(key: string): T | null {
  try {
    const cached = localStorage.getItem(key);
    if (cached) {
      const parsed: CachedData<T> = JSON.parse(cached);
      if (parsed.expiry && Date.now() < parsed.expiry) {
        return parsed.data;
      }
      // Clean up expired cache
      localStorage.removeItem(key);
    }
  } catch (e) {
    if (IS_DEV) {
      console.error(`[Wishlist] Failed to load cache for ${key}:`, e);
    }
    try {
      localStorage.removeItem(key);
    } catch {
      // Ignore cleanup errors
    }
  }
  return null;
}

/**
 * Generic function to save data to localStorage cache
 * @param key - localStorage key
 * @param data - data to cache
 */
export function saveToCache<T>(key: string, data: T): void {
  try {
    localStorage.setItem(
      key,
      JSON.stringify({
        data,
        expiry: Date.now() + CACHE_EXPIRY_MS
      })
    );
  } catch (e) {
    if (IS_DEV) {
      console.error(`[Wishlist] Failed to save cache for ${key}:`, e);
    }
  }
}

/**
 * Clear multiple cache keys from localStorage
 * @param keys - array of localStorage keys to clear
 */
export function clearCache(...keys: string[]): void {
  for (const key of keys) {
    try {
      localStorage.removeItem(key);
    } catch {
      // Ignore cleanup errors
    }
  }
}

/**
 * Clear all wishlist-related cache entries
 */
export function clearAllWishlistCache(): void {
  clearCache(
    STORAGE_KEY_ITEMS,
    STORAGE_KEY_IMAGES,
    STORAGE_KEY_PRODUCT_DATA,
    STORAGE_KEY_WISHLISTS
  );
}
